import React, { useState } from "react";
import YouTubeImportModal from "./components/YouTubeImportModal";
import YouTubeEditModal from "./components/YouTubeEditModal";
import { KalturaExternalMediaEntry } from "kaltura-typescript-client/api/types/KalturaExternalMediaEntry";
import { EditFormElement } from "@kaltura/mediaspace-shared-edit-entry";
import { StyledModal } from "./components/StyleComponents";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType, KmsTypeComponent } from "@kaltura/mediaspace-shared-types";

export interface YouTubeImportProps {
    open: boolean;
    onCancel: () => void;
    uiconfId: string;
    fieldsStructure: EditFormElement[];
    preSaveComponent?: KmsTypeComponent;
    postSaveComponent?: KmsTypeComponent;
}

/**
 * YouTube Import Component
 * This component handles the full flow of importing a YouTube video:
 * 1. Modal for entering YouTube URL
 * 2. Preview of the video
 * 3. Edit form for metadata
 * 4. Saving and redirecting to the entry page
 */

export const YouTubeImport: React.FC<YouTubeImportProps> = ({
    open,
    onCancel,
    uiconfId,
    fieldsStructure,
    preSaveComponent,
    postSaveComponent,
}) => {
    const [youtubeEntry, setYoutubeEntry] = useState<KalturaExternalMediaEntry | null>(null);
    const [showEditForm, setShowEditForm] = useState(false);
    const sendButtonAnalytics = useButtonAnalytics();

    // Handle the import button click from the modal
    const handleImport = (youtubeVideo: KalturaExternalMediaEntry) => {
        // Store the video data and entry ID
        setYoutubeEntry(youtubeVideo);
        setShowEditForm(true);
    };

    // Handle closing the edit form
    const handleFormClose = (shouldRedirect = true) => {
        setShowEditForm(false);
        setYoutubeEntry(null);

        if (onCancel) {
            onCancel();
        }

        // If shouldRedirect is true and youtubeEntry is set, redirect to the entry page
        if (shouldRedirect && youtubeEntry && youtubeEntry.id) {
            window.location.href = `/media/${youtubeEntry.id}`;
            return null;
        }
        else {
            //cancel the modal
            sendButtonAnalytics("Import from YouTube", ButtonClickAnalyticsType.CLOSE);
        }
    };

    const showEdit = showEditForm && !!youtubeEntry;
    // by default show the import modal, and if import finished successfully - show the edit form
    const modalContent = showEdit ? (
        <YouTubeEditModal
            open={open}
            onCancel={() => handleFormClose(false)}
            youtubeEntry={youtubeEntry}
            onClose={handleFormClose}
            fieldsStructure={fieldsStructure}
            preSaveComponent={preSaveComponent}
            postSaveComponent={postSaveComponent}
        />
    ) : (
        <YouTubeImportModal
            open={open}
            onCancel={() => handleFormClose(false)}
            onImport={handleImport}
            uiconfId={uiconfId}
        />
    );

    return (
        <StyledModal
            open={open}
            editSizedModal={showEdit}
            onClose={(_, reason) => {
                if (reason !== "backdropClick") {
                    onCancel();
                }
            }}
        >
            {modalContent}
        </StyledModal>
    );
};
